import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import useSEO from "../hooks/useSEO";
import ParticleNetwork from "../components/ParticleNetwork";
import { Reveal, StaggerGroup, StaggerItem } from "../components/Reveal";
import ServicesNav from "../components/services/ServicesNav";
import ServiceSection from "../components/services/ServiceSection";
import Process from "../components/home/Process";
import { SERVICE_CATEGORIES, SERVICE_DETAILS } from "../data/site";

export default function Services() {
  useSEO({
    title: "Services — ATS IT Solutions | IT Consulting, Software, Cloud, AI & Automation, DevOps, Security, Digital Growth",
    description:
      "Explore the eight capability areas of ATS IT Solutions: IT consulting, software & application development, cloud, AI, data & automation, DevOps, cybersecurity, managed IT support and digital marketing & growth.",
  });

  return (
    <div data-testid="services-page">
      <section className="relative pt-40 pb-16 md:pt-48 md:pb-20 overflow-hidden bg-navy-deep">
        <div className="absolute inset-0 z-0 opacity-60">
          <ParticleNetwork className="w-full h-full" density={0.6} />
        </div>
        <div className="absolute top-1/3 left-1/4 w-[500px] h-[500px] rounded-full bg-brand-orange/10 blur-[160px] z-0" />
        <div className="container-x relative z-10">
          <Reveal>
            <span className="overline">Technology Solutions by ATS IT Solutions</span>
          </Reveal>
          <Reveal delay={0.05}>
            <h1 className="font-head text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-white mt-4 max-w-4xl">
              Eight capability areas. <span className="text-gradient-orange">One accountable partner.</span>
            </h1>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 text-lg text-slate-300 max-w-2xl leading-relaxed">
              From strategy to shipped software, AI automation, digital growth and round-the-clock support, ATS organises everything it builds and runs into eight clear service lines — so you always know exactly what you're getting.
            </p>
          </Reveal>

          <StaggerGroup className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-3" stagger={0.06}>
            {SERVICE_CATEGORIES.map((c, i) => {
              const Icon = c.icon;
              return (
                <StaggerItem key={c.id}>
                  <a
                    href={`#${c.id}`}
                    data-testid={`services-overview-${c.id}`}
                    onClick={(e) => {
                      e.preventDefault();
                      const el = document.getElementById(c.id);
                      if (window.__lenis) window.__lenis.scrollTo(el, { offset: -100 });
                      else el?.scrollIntoView({ behavior: "smooth" });
                    }}
                    className="group glass rounded-2xl p-5 h-full flex flex-col gap-3 card-glow hover:border-brand-orange/40 transition-colors"
                  >
                    <span className="font-mono text-[10px] text-brand-orange tracking-widest">{String(i + 1).padStart(2, "0")}</span>
                    <Icon size={22} className="text-slate-300 group-hover:text-brand-orange transition-colors" />
                    <span className="font-head text-sm font-semibold text-white leading-snug">{c.title}</span>
                    <ArrowDown size={14} className="text-slate-500 mt-auto group-hover:translate-y-1 transition-transform" />
                  </a>
                </StaggerItem>
              );
            })}
          </StaggerGroup>
        </div>
      </section>

      <ServicesNav />

      {SERVICE_CATEGORIES.map((c, i) => (
        <ServiceSection key={c.id} cat={c} detail={SERVICE_DETAILS[c.id]} index={i} />
      ))}

      <Process />

      <section className="relative py-24 md:py-28 bg-navy-deep border-t border-white/10">
        <div className="container-x text-center flex flex-col items-center">
          <Reveal>
            <span className="overline">Partner with ATS IT Solutions</span>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-head text-3xl md:text-5xl font-semibold text-white max-w-3xl mt-4">
              Not sure which service you need? <span className="text-gradient-orange">Let's figure it out together.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-5 text-slate-400 max-w-xl">A free consultation call with the ATS team — we'll map your challenge to the right capability and a clear next step.</p>
          </Reveal>
          <Reveal delay={0.15}>
            <Link to="/contact" data-testid="services-page-cta" className="btn-primary mt-8 text-base">
              Get a Free Consultation <ArrowUpRight size={18} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
